import {
  Eye,
  EyeOff,
  Image as ImageIcon,
  Pencil,
  Plus,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminLayout from '../../components/admin/AdminLayout';
import CuponsEvento from '../../components/admin/CuponsEvento';
import { ResponsiveGrid } from '../../components/admin/responsive';
import { api } from '../../services/api';

// Comprime imagem para max 800px de largura, JPEG 0.8 — capa de evento (formato paisagem/banner)
function comprimirImagem(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 800;
        let { width, height } = img;
        if (width > MAX) {
          height = Math.round((height * MAX) / width);
          width = MAX;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Normalize amounts that appear to be doubled (> R$ 10.000)
function normalizarValor(centavos) {
  if (!centavos) return 0;
  let valor = Number(centavos);
  // If > 100.000 centavos (R$ 1.000+), likely doubled by 100
  if (valor > 100000) {
    valor = Math.round(valor / 100);
  }
  return valor;
}

function formatarData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

function StatusEvento({ status }) {
  const map = {
    PUBLISHED: { label: 'Publicado', bg: '#dcfce7', color: '#166534' },
    DRAFT: { label: 'Rascunho', bg: '#fef9c3', color: '#854d0e' },
    ENDED: { label: 'Encerrado', bg: '#f1f5f9', color: '#64748b' },
    CANCELLED: { label: 'Cancelado', bg: '#fee2e2', color: '#991b1b' },
    ARCHIVED: { label: 'Arquivado', bg: '#f3f4f6', color: '#6b7280' },
  };
  const s = map[status] ?? { label: status, bg: '#f1f5f9', color: '#64748b' };
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 900,
        letterSpacing: '0.08em',
        padding: '3px 10px',
        borderRadius: 99,
        background: s.bg,
        color: s.color,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  );
}

function ModalShell({ title, onClose, maxWidth = 480, children }) {
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={{ ...styles.modal, maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            style={styles.btnClose}
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormaPagamentoField({ pixOnly, onChange }) {
  const opcoes = [
    {
      valor: false,
      titulo: 'PIX e Cartão de Crédito',
      descricao: 'A leitora escolhe como pagar (padrão)',
    },
    {
      valor: true,
      titulo: 'Somente PIX',
      descricao: 'Esconde a opção de cartão no checkout',
    },
  ];
  return (
    <div>
      <p style={styles.label}>Forma de pagamento aceita</p>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          marginTop: 6,
        }}
      >
        {opcoes.map((op) => (
          <label
            key={String(op.valor)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '10px 14px',
              border: `1px solid ${pixOnly === op.valor ? '#ff2d6c' : '#e2e8f0'}`,
              borderRadius: 10,
              cursor: 'pointer',
              background: pixOnly === op.valor ? '#fff5f7' : '#fff',
            }}
          >
            <input
              type="radio"
              name="formaPagamentoEspecial"
              checked={pixOnly === op.valor}
              onChange={() => onChange(op.valor)}
              style={{ marginTop: 3 }}
            />
            <div>
              <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                {op.titulo}
              </div>
              <div style={{ fontSize: 11.5, color: '#94a3b8' }}>
                {op.descricao}
              </div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function CapaField({ value, onChange, setErro }) {
  const fileRef = useRef();

  async function handleImagem(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setErro('Selecione uma imagem.');
      return;
    }
    try {
      const base64 = await comprimirImagem(file);
      onChange(base64);
      setErro('');
    } catch {
      setErro('Falha ao processar imagem.');
    }
  }

  return (
    <div>
      <p style={styles.label}>Capa do evento</p>
      <div
        onClick={() => fileRef.current?.click()}
        style={{
          position: 'relative',
          marginTop: 6,
          aspectRatio: '16/9',
          borderRadius: 12,
          overflow: 'hidden',
          border: '2px dashed #e2e8f0',
          background: '#f8fafc',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {value ? (
          <img
            src={value}
            alt="Capa do evento"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              color: '#94a3b8',
            }}
          >
            <Upload size={20} />
            <span style={{ fontSize: 11, fontWeight: 700 }}>
              Clique para enviar
            </span>
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImagem}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          style={{
            marginTop: 6,
            background: 'none',
            border: 'none',
            color: '#dc2626',
            fontSize: 11,
            fontWeight: 700,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Remover imagem
        </button>
      )}
    </div>
  );
}

const FORM_VAZIO = {
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  location: '',
  amount: '',
  pixOnly: false,
  capacity: '',
  coverImageBase64: '',
  habilitarExtra: false,
  nomeExtra: '',
  descricaoExtra: '',
  valorExtra: 0,
  addonId: null,
};

export default function EventosEspeciais() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const [modalNovo, setModalNovo] = useState(false);
  const [novoEvento, setNovoEvento] = useState(FORM_VAZIO);

  const [modalEditar, setModalEditar] = useState(null);
  const [editForm, setEditForm] = useState(FORM_VAZIO);

  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('ativos');
  const [eventoCupons, setEventoCupons] = useState(null);

  async function carregar() {
    try {
      setCarregando(true);
      const { data } = await api.get('/admin/eventos-especiais');
      setEventos(data);
    } catch {
      setErro('Erro ao carregar eventos Lily Dark.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
  }, []);

  function mostrarFeedback(msg, tipo = 'ok') {
    setFeedback({ msg, tipo });
    setTimeout(() => setFeedback(null), 3500);
  }

  async function toggleStatus(evento) {
    const novoStatus = evento.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED';
    try {
      await api.patch(`/admin/eventos-especiais/${evento.id}`, {
        status: novoStatus,
      });
      mostrarFeedback(
        `Evento ${novoStatus === 'PUBLISHED' ? 'publicado' : 'voltado para rascunho'}!`,
      );
      carregar();
    } catch {
      mostrarFeedback('Erro ao atualizar status.', 'erro');
    }
  }

  async function criarEventoEspecial(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.post('/admin/eventos-especiais', {
        title: novoEvento.title,
        description: novoEvento.description || null,
        startDate: novoEvento.startDate,
        endDate: novoEvento.endDate || null,
        location: novoEvento.location,
        amountInCents: Math.round(parseFloat(novoEvento.amount || '0') * 100),
        pixOnly: novoEvento.pixOnly,
        capacity:
          novoEvento.capacity === '' ? null : Number(novoEvento.capacity),
        coverImageBase64: novoEvento.coverImageBase64 || null,
      });
      setModalNovo(false);
      setNovoEvento(FORM_VAZIO);
      mostrarFeedback('Evento Lily Dark criado!');
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao criar evento Lily Dark.',
        'erro',
      );
    } finally {
      setSalvando(false);
    }
  }

  function abrirEditar(evento) {
    const primeiroAddOn = evento.addOns?.[0];
    setEditForm({
      title: evento.title,
      description: evento.description || '',
      startDate: toDatetimeLocal(evento.startDate),
      endDate: toDatetimeLocal(evento.endDate),
      location: evento.location,
      amount: (normalizarValor(evento.amountInCents) / 100).toFixed(2),
      pixOnly: evento.pixOnly || false,
      capacity: evento.capacity ?? '',
      coverImageBase64: evento.coverImageBase64 || '',
      habilitarExtra: !!primeiroAddOn,
      nomeExtra: primeiroAddOn?.name || '',
      descricaoExtra: primeiroAddOn?.description || '',
      valorExtra: primeiroAddOn
        ? parseFloat((primeiroAddOn.priceInCents / 100).toFixed(2))
        : 0,
      addonId: primeiroAddOn?.id || null,
    });
    setModalEditar(evento.id);
  }

  async function salvarEditar(e) {
    e.preventDefault();
    setSalvando(true);
    try {
      await api.patch(`/admin/eventos-especiais/${modalEditar}`, {
        title: editForm.title,
        description: editForm.description || null,
        startDate: editForm.startDate,
        endDate: editForm.endDate || null,
        location: editForm.location,
        amountInCents: Math.round(parseFloat(editForm.amount || '0') * 100),
        pixOnly: editForm.pixOnly,
        capacity: editForm.capacity === '' ? null : Number(editForm.capacity),
        coverImageBase64: editForm.coverImageBase64 || null,
      });

      // Salvar add-ons - GARANTE apenas 1 add-on por evento
      if (
        editForm.habilitarExtra &&
        editForm.nomeExtra &&
        editForm.valorExtra > 0
      ) {
        // SEMPRE deletar TODOS os add-ons antigos antes de criar novo
        console.log('[ADDON DEBUG] Deletando TODOS os add-ons antigos...');
        try {
          const allAddons = await api.get(
            `/admin/eventos-especiais/${modalEditar}/addons`,
          );
          for (const addon of allAddons.data || []) {
            await api.delete(
              `/admin/eventos-especiais/${modalEditar}/addons/${addon.id}`,
            );
            console.log('[ADDON DEBUG] Deletado:', addon.id);
          }
        } catch (err) {
          console.log(
            '[ADDON DEBUG] Erro ao limpar antigos (continuando...):',
            err.message,
          );
        }

        const priceInCents = Math.round(parseFloat(editForm.valorExtra) * 100);

        // Criar NOVO add-on (único)
        console.log('[ADDON DEBUG] Criando novo add-on único:', {
          name: editForm.nomeExtra,
          description: editForm.descricaoExtra,
          priceInCents,
        });
        const response = await api.post(
          `/admin/eventos-especiais/${modalEditar}/addons`,
          {
            name: editForm.nomeExtra,
            description: editForm.descricaoExtra,
            priceInCents,
          },
        );
        console.log('[ADDON DEBUG] ✅ Add-on criado:', response.data);
      } else if (!editForm.habilitarExtra) {
        // DESABILITAR: Deletar TODOS os add-ons
        console.log(
          '[ADDON DEBUG] Desabilitando - Deletando TODOS os add-ons...',
        );
        try {
          const allAddons = await api.get(
            `/admin/eventos-especiais/${modalEditar}/addons`,
          );
          for (const addon of allAddons.data || []) {
            await api.delete(
              `/admin/eventos-especiais/${modalEditar}/addons/${addon.id}`,
            );
            console.log('[ADDON DEBUG] ✅ Deletado:', addon.id);
          }
        } catch (err) {
          console.log(
            '[ADDON DEBUG] Erro ao deletar (continuando...):',
            err.message,
          );
        }
      }

      setModalEditar(null);
      mostrarFeedback('Evento Lily Dark atualizado!');
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao atualizar evento Lily Dark.',
        'erro',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function excluirEventoEspecial(evento) {
    if (!confirm(`Excluir "${evento.title}"? Esta ação não pode ser desfeita.`))
      return;
    try {
      await api.delete(`/admin/eventos-especiais/${evento.id}`);
      mostrarFeedback('Evento Lily Dark excluído.');
      carregar();
    } catch (err) {
      mostrarFeedback(
        err.response?.data?.error || 'Erro ao excluir evento Lily Dark.',
        'erro',
      );
    }
  }

  if (carregando)
    return (
      <AdminLayout>
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 64 }}>
          Carregando...
        </div>
      </AdminLayout>
    );

  return (
    <AdminLayout>
      <div style={styles.header}>
        <div>
          <h1 style={styles.h1}>Leituras Complementares</h1>
          <p style={styles.subtitle}>
            Eventos especiais isolados do calendário mensal
          </p>
        </div>
        {filtroStatus === 'ativos' && (
          <button onClick={() => setModalNovo(true)} style={styles.btnPrimary}>
            <Plus size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
            Novo evento
          </button>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 0,
          marginBottom: 24,
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <button
          onClick={() => setFiltroStatus('ativos')}
          style={{
            flex: '0 1 auto',
            background: 'none',
            border: 'none',
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: filtroStatus === 'ativos' ? 700 : 500,
            color: filtroStatus === 'ativos' ? '#0f172a' : '#94a3b8',
            cursor: 'pointer',
            borderBottom:
              filtroStatus === 'ativos' ? '3px solid #d946ef' : 'none',
            transition: 'all 150ms ease',
          }}
        >
          Gerenciar
        </button>
        <button
          onClick={() => setFiltroStatus('arquivados')}
          style={{
            flex: '0 1 auto',
            background: 'none',
            border: 'none',
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: filtroStatus === 'arquivados' ? 700 : 500,
            color: filtroStatus === 'arquivados' ? '#0f172a' : '#94a3b8',
            cursor: 'pointer',
            borderBottom:
              filtroStatus === 'arquivados' ? '3px solid #6b7280' : 'none',
            transition: 'all 150ms ease',
          }}
        >
          📚 Histórico (Passados)
        </button>
      </div>

      {erro && <div style={styles.erro}>{erro}</div>}
      {feedback && (
        <div
          style={{
            ...styles.feedback,
            background: feedback.tipo === 'erro' ? '#fee2e2' : '#dcfce7',
            color: feedback.tipo === 'erro' ? '#991b1b' : '#166534',
          }}
        >
          {feedback.msg}
        </div>
      )}

      {filtroStatus === 'arquivados' && (
        <div
          style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: 8,
            padding: 12,
            marginBottom: 16,
            fontSize: 13,
            color: '#166534',
            lineHeight: 1.5,
          }}
        >
          📚 <strong>Histórico de Eventos</strong> — Eventos que já ocorreram
          (como "Lily on top"). Todos os registros de compra são preservados.
          Clique em "Inscrições" para ver detalhes.
        </div>
      )}

      {eventos.filter((e) => {
        if (filtroStatus === 'arquivados') return e.status === 'ARCHIVED';
        return e.status !== 'ARCHIVED';
      }).length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            color: '#94a3b8',
            padding: 48,
            background: '#fff',
            borderRadius: 16,
            border: '1px solid #f1f5f9',
          }}
        >
          Nenhum evento Lily Dark cadastrado ainda.
        </div>
      ) : (
        <ResponsiveGrid
          config={{
            mobileCols: 1,
            tabletCols: 1,
            desktopCols: 1,
            gap: 16,
          }}
        >
          {eventos
            .filter((e) => {
              if (filtroStatus === 'arquivados') return e.status === 'ARCHIVED';
              return e.status !== 'ARCHIVED';
            })
            .map((evento) => (
              <div key={evento.id} style={styles.card}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div
                    style={{ display: 'flex', gap: 14, minWidth: 0, flex: 1 }}
                  >
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 12,
                        overflow: 'hidden',
                        background: '#f8fafc',
                        border: '1px solid #f1f5f9',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {evento.coverImageBase64 ? (
                        <img
                          src={evento.coverImageBase64}
                          alt={evento.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <ImageIcon size={20} color="#cbd5e1" />
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 900,
                            fontSize: 15,
                            color: '#0f172a',
                          }}
                        >
                          {evento.title}
                        </span>
                        <StatusEvento status={evento.status} />
                        {evento.pixOnly && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 900,
                              letterSpacing: '0.08em',
                              padding: '3px 10px',
                              borderRadius: 99,
                              background: '#dbeafe',
                              color: '#1e40af',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            SOMENTE PIX
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: '#94a3b8',
                          marginTop: 3,
                          lineHeight: 1.6,
                        }}
                      >
                        {formatarData(evento.startDate)} · {evento.location}
                        <br />
                        <span style={{ color: '#0f172a', fontWeight: 700 }}>
                          R${' '}
                          {(
                            normalizarValor(evento.amountInCents) / 100
                          ).toFixed(2)}
                        </span>
                        {evento.addOns &&
                          evento.addOns.length > 0 &&
                          evento.addOns[0].active && (
                            <span
                              style={{
                                color: '#ff2d6c',
                                fontWeight: 700,
                                marginLeft: 8,
                                padding: '2px 8px',
                                background: '#fff5f7',
                                borderRadius: 4,
                                display: 'inline-block',
                              }}
                            >
                              ✦ +R${' '}
                              {(evento.addOns[0].priceInCents / 100).toFixed(2)}
                            </span>
                          )}
                        <br />
                        {evento.totalCompras} confirmada(s)
                        {evento.totalPendentes > 0 && (
                          <span style={{ color: '#d97706', fontWeight: 600 }}>
                            {' '}
                            · {evento.totalPendentes} pendente(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={() =>
                        navigate(
                          `/admin/eventos-especiais/${evento.id}/inscricoes`,
                        )
                      }
                      style={styles.btnSecondary}
                    >
                      <Users
                        size={13}
                        style={{ marginRight: 5, verticalAlign: -2 }}
                      />
                      Inscrições
                    </button>
                    <button
                      onClick={() => setEventoCupons(evento)}
                      style={styles.btnSecondary}
                    >
                      Cupons
                    </button>
                    <button
                      onClick={() => abrirEditar(evento)}
                      style={styles.btnSecondary}
                    >
                      <Pencil
                        size={13}
                        style={{ marginRight: 5, verticalAlign: -2 }}
                      />
                      Editar
                    </button>
                    <button
                      onClick={() => toggleStatus(evento)}
                      style={{
                        ...styles.btnSecondary,
                        color:
                          evento.status === 'PUBLISHED' ? '#854d0e' : '#166534',
                      }}
                    >
                      {evento.status === 'PUBLISHED' ? (
                        <EyeOff
                          size={13}
                          style={{ marginRight: 5, verticalAlign: -2 }}
                        />
                      ) : (
                        <Eye
                          size={13}
                          style={{ marginRight: 5, verticalAlign: -2 }}
                        />
                      )}
                      {evento.status === 'PUBLISHED'
                        ? 'Despublicar'
                        : 'Publicar'}
                    </button>
                    <button
                      onClick={() => excluirEventoEspecial(evento)}
                      style={{
                        ...styles.btnSecondary,
                        color: '#dc2626',
                        borderColor: '#fecaca',
                      }}
                    >
                      <Trash2
                        size={13}
                        style={{ marginRight: 5, verticalAlign: -2 }}
                      />
                      Excluir
                    </button>
                  </div>
                </div>
              </div>
            ))}
        </ResponsiveGrid>
      )}

      {eventoCupons && (
        <ModalShell
          title={`Cupons · ${eventoCupons.title}`}
          onClose={() => setEventoCupons(null)}
          maxWidth={760}
        >
          <CuponsEvento
            specialEventId={eventoCupons.id}
            empilhado
            precos={[
              { label: 'Ingresso', centavos: eventoCupons.amountInCents },
            ]}
          />
        </ModalShell>
      )}

      {modalNovo && (
        <ModalShell
          title="Criar evento Lily Dark"
          onClose={() => setModalNovo(false)}
          maxWidth={520}
        >
          <form
            onSubmit={criarEventoEspecial}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <CapaField
              value={novoEvento.coverImageBase64}
              onChange={(v) =>
                setNovoEvento({ ...novoEvento, coverImageBase64: v })
              }
              setErro={setErro}
            />
            <label style={styles.label}>
              Título
              <input
                required
                style={styles.input}
                value={novoEvento.title}
                onChange={(e) =>
                  setNovoEvento({ ...novoEvento, title: e.target.value })
                }
                placeholder="Ex: Sarau literário de aniversário"
              />
            </label>
            <label style={styles.label}>
              Descrição
              <textarea
                style={{ ...styles.input, minHeight: 70, resize: 'vertical' }}
                value={novoEvento.description}
                onChange={(e) =>
                  setNovoEvento({ ...novoEvento, description: e.target.value })
                }
                placeholder="Detalhes do evento (opcional)"
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Início
                <input
                  required
                  type="datetime-local"
                  style={styles.input}
                  value={novoEvento.startDate}
                  onChange={(e) =>
                    setNovoEvento({ ...novoEvento, startDate: e.target.value })
                  }
                />
              </label>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Fim
                <input
                  type="datetime-local"
                  style={styles.input}
                  value={novoEvento.endDate}
                  onChange={(e) =>
                    setNovoEvento({ ...novoEvento, endDate: e.target.value })
                  }
                />
              </label>
            </div>
            <label style={styles.label}>
              Local
              <input
                required
                style={styles.input}
                value={novoEvento.location}
                onChange={(e) =>
                  setNovoEvento({ ...novoEvento, location: e.target.value })
                }
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Valor (R$)
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  max="9999.99"
                  style={styles.input}
                  value={novoEvento.amount}
                  onChange={(e) =>
                    setNovoEvento({ ...novoEvento, amount: e.target.value })
                  }
                  placeholder="55.00"
                />
                {novoEvento.amount && parseFloat(novoEvento.amount) > 1000 && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
                    ⚠ Valor parece estar em centavos. Use reais (ex: 55.00)
                  </div>
                )}
              </label>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Capacidade{' '}
                <span
                  style={{
                    fontWeight: 400,
                    textTransform: 'none',
                    fontSize: 10,
                    color: '#94a3b8',
                  }}
                >
                  (vazio = ilimitado)
                </span>
                <input
                  type="number"
                  min={1}
                  style={styles.input}
                  value={novoEvento.capacity}
                  onChange={(e) =>
                    setNovoEvento({ ...novoEvento, capacity: e.target.value })
                  }
                  placeholder="Ilimitado"
                />
              </label>
            </div>
            <FormaPagamentoField
              pixOnly={novoEvento.pixOnly}
              onChange={(v) => setNovoEvento({ ...novoEvento, pixOnly: v })}
            />
            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setModalNovo(false)}
                style={styles.btnSecondary}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={salvando}
                style={styles.btnPrimary}
              >
                {salvando ? 'Salvando...' : 'Criar evento'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {modalEditar && (
        <ModalShell
          title="Editar evento Lily Dark"
          onClose={() => setModalEditar(null)}
          maxWidth={520}
        >
          <form
            onSubmit={salvarEditar}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <CapaField
              value={editForm.coverImageBase64}
              onChange={(v) =>
                setEditForm({ ...editForm, coverImageBase64: v })
              }
              setErro={setErro}
            />
            <label style={styles.label}>
              Título
              <input
                required
                style={styles.input}
                value={editForm.title}
                onChange={(e) =>
                  setEditForm({ ...editForm, title: e.target.value })
                }
              />
            </label>
            <label style={styles.label}>
              Descrição
              <textarea
                style={{ ...styles.input, minHeight: 70, resize: 'vertical' }}
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Início
                <input
                  required
                  type="datetime-local"
                  style={styles.input}
                  value={editForm.startDate}
                  onChange={(e) =>
                    setEditForm({ ...editForm, startDate: e.target.value })
                  }
                />
              </label>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Fim
                <input
                  type="datetime-local"
                  style={styles.input}
                  value={editForm.endDate}
                  onChange={(e) =>
                    setEditForm({ ...editForm, endDate: e.target.value })
                  }
                />
              </label>
            </div>
            <label style={styles.label}>
              Local
              <input
                required
                style={styles.input}
                value={editForm.location}
                onChange={(e) =>
                  setEditForm({ ...editForm, location: e.target.value })
                }
              />
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Valor (R$)
                <input
                  required
                  type="number"
                  step="0.01"
                  min="0"
                  max="9999.99"
                  style={styles.input}
                  value={editForm.amount}
                  onChange={(e) =>
                    setEditForm({ ...editForm, amount: e.target.value })
                  }
                />
                {editForm.amount && parseFloat(editForm.amount) > 1000 && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
                    ⚠ Valor parece estar em centavos. Use reais (ex: 55.00)
                  </div>
                )}
              </label>
              <label style={{ ...styles.label, flex: '1 1 140px' }}>
                Capacidade{' '}
                <span
                  style={{
                    fontWeight: 400,
                    textTransform: 'none',
                    fontSize: 10,
                    color: '#94a3b8',
                  }}
                >
                  (vazio = ilimitado)
                </span>
                <input
                  type="number"
                  min={1}
                  style={styles.input}
                  value={editForm.capacity}
                  onChange={(e) =>
                    setEditForm({ ...editForm, capacity: e.target.value })
                  }
                  placeholder="Ilimitado"
                />
              </label>
            </div>
            <FormaPagamentoField
              pixOnly={editForm.pixOnly}
              onChange={(v) => setEditForm({ ...editForm, pixOnly: v })}
            />

            {/* Valor Extra - Toggle e Campos */}
            <div
              style={{
                borderTop: '1px solid #f1f5f9',
                paddingTop: 14,
                marginTop: 14,
              }}
            >
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                <input
                  type="checkbox"
                  checked={editForm.habilitarExtra}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      habilitarExtra: e.target.checked,
                    })
                  }
                  style={{
                    cursor: 'pointer',
                    width: 18,
                    height: 18,
                  }}
                />
                <span style={{ fontWeight: 600 }}>
                  Adicionar Valor Extra (Cinema, Transporte, etc)
                </span>
              </label>

              {/* Campos do Valor Extra - Com visual destacado */}
              {editForm.habilitarExtra && (
                <div
                  style={{
                    gap: 12,
                    flexDirection: 'column',
                    display: 'flex',
                    padding: '14px',
                    background: '#fff5f7',
                    borderRadius: 8,
                    border: '2px solid #ff2d6c',
                    borderLeft: '4px solid #ff2d6c',
                  }}
                >
                  <label style={styles.label}>
                    Nome do Extra
                    <input
                      value={editForm.nomeExtra}
                      onChange={(e) =>
                        setEditForm({ ...editForm, nomeExtra: e.target.value })
                      }
                      style={styles.input}
                      placeholder="ex: Ingresso Cinema"
                    />
                  </label>
                  <label style={styles.label}>
                    Descrição / Justificativa
                    <input
                      value={editForm.descricaoExtra}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          descricaoExtra: e.target.value,
                        })
                      }
                      style={styles.input}
                      placeholder="ex: Sessão exclusiva no cinema com pipoca"
                    />
                  </label>
                  <label style={styles.label}>
                    Valor Extra (R$)
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.valorExtra}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          valorExtra: parseFloat(e.target.value) || 0,
                        })
                      }
                      style={styles.input}
                      placeholder="25.00"
                    />
                  </label>
                  {editForm.nomeExtra && editForm.valorExtra > 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        color: '#475569',
                        padding: '8px 0',
                        borderTop: '1px solid #e2e8f0',
                      }}
                    >
                      <strong>Preview:</strong> R${' '}
                      {parseFloat(editForm.amount || 0) +
                        parseFloat(editForm.valorExtra || 0)}{' '}
                      (R$ {editForm.amount} + R$ {editForm.valorExtra} extra)
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                gap: 10,
                justifyContent: 'flex-end',
                marginTop: 8,
              }}
            >
              <button
                type="button"
                onClick={() => setModalEditar(null)}
                style={styles.btnSecondary}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={salvando}
                style={styles.btnPrimary}
              >
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </AdminLayout>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 12,
  },
  h1: { fontSize: 22, fontWeight: 900, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: 13, color: '#94a3b8', margin: '4px 0 0' },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: 20,
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 4px rgba(0,0,0,.04)',
  },
  btnPrimary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    background: '#ff2d6c',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 900,
    cursor: 'pointer',
    letterSpacing: '0.05em',
    minHeight: '44px',
    transition: 'background-color 0.2s',
  },
  btnSecondary: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 24px',
    background: '#f8fafc',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    minHeight: '44px',
    transition: 'background-color 0.2s',
  },
  btnClose: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    color: '#64748b',
    width: 30,
    height: 30,
    flexShrink: 0,
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: 16,
    boxSizing: 'border-box',
  },
  modal: {
    background: '#fff',
    borderRadius: 20,
    padding: 28,
    width: '100%',
    maxWidth: 540,
    boxShadow: '0 20px 60px rgba(0,0,0,.15)',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 20,
  },
  modalTitle: { fontSize: 18, fontWeight: 900, color: '#0f172a', margin: 0 },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    fontSize: 10,
    fontWeight: 900,
    color: '#94a3b8',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  input: {
    padding: '12px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 16,
    color: '#0f172a',
    outline: 'none',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '44px',
    transition: 'border-color 0.2s',
  },
  erro: {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '10px 16px',
    borderRadius: 10,
    marginBottom: 16,
    fontSize: 13,
  },
  feedback: {
    padding: '10px 16px',
    borderRadius: 10,
    marginBottom: 16,
    fontSize: 13,
    fontWeight: 700,
  },
};
